import React, { useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { remittanceActions } from "../../state/remittanceSlice";
import { CrossIcon, DownIcon, Equal, InfoIcon, MoneyReceive } from "../../../../assets/Icons";
import { formatNumber, lastTwoDecimal, logApiErrorToSentry } from "../../utils";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ExchangeRateBlack } from "../../images/svg";
import Button from "../../../../components/ui/Button";
import SummaryRow from "../../components/ui/SummaryRow";
import SubSummaryRow from "../../components/ui/SubSummaryRow";
import { AppStrings } from "../../utils";
import { styles } from "./styles";
import {QuoteManagerModal} from "../../components/modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { remittanceApi } from "../../api/remittanceApi";
import MixpanelConfig from "../../../../constants/MixpanelConfig";

const CalculationSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator } = useAppSelector((state) => state.remittance);

  const [showInfo, setShowInfo] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // ===== Computed Values =====
  const taxesAndFees =
    calculator?.getTransactionSummary?.data?.total_tax +
    calculator?.getTransactionSummary?.data?.nostro_charges +
    calculator?.getTransactionSummary?.data?.nostro_charges_new +
    calculator?.getTransactionSummary?.data?.hop_fees +
    calculator?.getTransactionSummary?.data?.tcs;

  const vendorFee = useMemo(() => {
    return calculator?.amountCalculation?.roundOff
      ? calculator?.getTransactionSummary?.data?.totalPayInCeils?.find((item: any) => item.id === 10)?.hop_fee
      : calculator?.getTransactionSummary?.data?.hop_fees;
  }, [calculator?.amountCalculation?.roundOff, calculator?.getTransactionSummary?.data]);

  const totalYouPay = useMemo(() => {
    return calculator?.amountCalculation?.roundOff
      ? calculator?.getTransactionSummary?.data?.totalPayInCeils?.find((item: any) => item.id === 10)?.total_pay
      : calculator?.getTransactionSummary?.data?.total_pay;
  }, [calculator?.amountCalculation?.roundOff, calculator?.getTransactionSummary?.data]);


  const proceedButtonDisable =
    calculator?.getTransactionSummary?.loading ||
    calculator?.fxRates?.loading ||
    calculator?.cardRate?.loading ||
    calculator?.getTransactionSummary?.error?.type ||
    !calculator?.amountCalculation?.purposeCode ||
    !calculator?.amountCalculation?.user_send ||
    !calculator?.amountCalculation?.reciepient_recieve ||
    !calculator?.amountCalculation?.conversion_rate ||
    !calculator?.amountCalculation?.referrerVendor ||
    !calculator?.amountCalculation?.dest_currency;

  const sendMoneyProceed = async () => {
    MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_SendMoney_Proceed, {
      authState: authState,
      remitterState: remitterState
    });
    const formData = {
      userKey: authState?.currentUser?.vendor_key,
      baseCurrency: calculator?.amountCalculation?.base_currency,
      destCurrency: calculator?.amountCalculation?.dest_currency,
    };
    try {
      dispatch(remittanceActions.setLockBookRate({ loading: true, error: null }));
      await remittanceApi.getLockBookRate(formData);
      dispatch(remittanceActions.setLockBookRate({ loading: false, error: null }));
      dispatch(remittanceActions.setActiveStepNumber(2));
      dispatch(remittanceActions.resetRemitterState());
      navigate("/send/remitter");
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error?.type || AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      dispatch(remittanceActions.setLockBookRate({ loading: false, error: errorMsg }));
      logApiErrorToSentry(err, "sendMoneyProceed");
    }
  };
    
  return (
    <div className={styles.calculationSection.wrapper}>

      <div className={styles.calculationSection.heading}>
        {AppStrings.CALCULATION_SECTION.HEADING}
      </div>

      <div className={styles.calculationSection.container}>

        {/* YOU SEND */}
        <SummaryRow
          icon={<div className="rotate-180"><DownIcon fillColor="rotate-90" /></div>}
          title={AppStrings.CALCULATION_SECTION.YOU_SEND}
          amount={formatNumber(calculator?.getTransactionSummary?.data?.user_send)}
          currency="INR"
            loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
        />

        {/* EXCHANGE RATE */}
        <SummaryRow
          icon={<div className="bg-neutral-900 w-8 h-8 rounded-full flex items-center justify-center shadow-sm"><ExchangeRateBlack /></div>}
          title={AppStrings.CALCULATION_SECTION.EXCHANGE_RATE}
          amount={formatNumber(calculator?.getTransactionSummary?.data?.conversion_rate)}
          currency="INR"
          loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
        />

        {/* TAXES & FEES */}
        <SummaryRow
          icon={<DownIcon />}
          title={AppStrings.CALCULATION_SECTION.TAXES_AND_FEES}
          amount={formatNumber(taxesAndFees?.toFixed(2))}
          currency="INR"
          loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
        />

        {/* SUB SUMMARY ROWS */}
        <div className={styles.calculationSection.subSectionWrapper}>
          <SubSummaryRow
            title={AppStrings.CALCULATION_SECTION.GST}
            amount={formatNumber(calculator?.getTransactionSummary?.data?.total_tax)}
            currency="INR"
            loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
          />

          <SubSummaryRow
            title={calculator?.amountCalculation?.purposeCode === "S0305"
              ? AppStrings.CALCULATION_SECTION.BANK_AND_CONVENIENCE_FEE
              : AppStrings.CALCULATION_SECTION.BANK_FEE}
            amount={formatNumber(calculator?.getTransactionSummary?.data?.nostro_charges)}
            currency="INR"
            loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
          />

          <SubSummaryRow
            title={AppStrings.CALCULATION_SECTION.NOSTRO}
            amount={formatNumber(calculator?.getTransactionSummary?.data?.nostro_charges_new)}
            currency="INR"
            loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
          >
            <div
              className={styles.calculationSection.nostroToggle}
              onClick={() =>
                dispatch(
                  remittanceActions.setAmountCalculation({
                    isBene: calculator?.amountCalculation?.isBene ? false : true,
                  })
                )
              }
            >
              {(calculator?.amountCalculation?.purposeCode === "S0305" || !calculator?.amountCalculation?.purposeCode) && (
                <div className={!calculator?.amountCalculation?.isBene
                  ? styles.calculationSection.nostroActive
                  : styles.calculationSection.nostroInactive}>
                  Our
                </div>
              )}

              <div className={calculator?.amountCalculation?.isBene
                ? styles.calculationSection.nostroActive
                : styles.calculationSection.nostroInactive}>
                Bene
              </div>
            </div>
          </SubSummaryRow>

          <SubSummaryRow
            title={AppStrings.CALCULATION_SECTION.VENDOR_FEE}
            amount={formatNumber(lastTwoDecimal(vendorFee))}
            currency="INR"
            loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
          />

          {/* TCS */}
          <div className={styles.calculationSection.tcsRow}>
            <div className={styles.calculationSection.tcsLeft}>
              <div className="mr-3" />
              <p className="mr-2">{AppStrings.CALCULATION_SECTION.TCS}</p>

              <div
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
                className="w-full"
              >
                <InfoIcon fillColor="#8D8D8D" />
              </div>

              {showInfo && (
                <div className={styles.calculationSection.tcsTooltip}>
                  <p className="text-sm text-gray-700">
                    {AppStrings.CALCULATION_SECTION.TCS_TOOLTIP}
                  </p>
                </div>
              )}
            </div>

            {(calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading) ? (
              <Skeleton width={100} height="100%" />
            ) : (
              <div className={styles.calculationSection.tcsRight}>
                <div className={styles.calculationSection.tcsAmount}>
                  {formatNumber(calculator?.getTransactionSummary?.data?.tcs)}
                </div>
                <div className={styles.calculationSection.currency}>INR</div>
              </div>
            )}
          </div>

          {/* PAN */}
          <div className={styles.calculationSection.panRow}>
            <p>PAN:</p>
            <p
              onClick={() => dispatch(remittanceActions.setModalType("PAN_DECLARATION"))}
              className={
                calculator?.amountCalculation?.pan_number === ""
                  ? styles.calculationSection.panAdd
                  : styles.calculationSection.panSelected
              }
            >
              {calculator?.amountCalculation?.pan_number !== ""
                ? calculator?.amountCalculation?.pan_number
                : AppStrings.CALCULATION_SECTION.ADD_DECLARATION}
            </p>

            {calculator?.amountCalculation?.pan_number !== "" && (
              <CrossIcon
                onClick={() =>
                  dispatch(remittanceActions.setAmountCalculation({ pan_number: "", declare_amount: "" }))
                }
                className="ml-4 cursor-pointer"
                width={13}
                height={13}
              />
            )}
          </div>
        </div>

        {/* YOU PAY */}
        <div className="border border-gray-500 rounded-full my-4" />
        <SummaryRow
          icon={<div className="rotate-180"><Equal /></div>}
          title={AppStrings.CALCULATION_SECTION.YOU_PAY}
          amount={formatNumber(totalYouPay)}
          currency="INR"
            loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
        >
          <div className="flex flex-row items-center justify-between ml-3">
            <button
              onClick={() =>
                dispatch(remittanceActions.setAmountCalculation({ roundOff: !calculator?.amountCalculation?.roundOff }))
              }
              className={`w-full flex justify-center items-center p-1 rounded-md ${calculator?.amountCalculation?.roundOff ? "bg-primary-100 text-white cursor-pointer" : "bg-[#E3E3E3] text-black"
                } ${calculator?.amountCalculation?.purposeCode !== "S0305" ? "cursor-not-allowed opacity-15" : ""}`}
              disabled={calculator?.amountCalculation?.purposeCode !== "S0305" ? true : false}
            >
              Round Off
            </button>
          </div>
        </SummaryRow>

        {/* THEY RECEIVE */}
        <div className="-mt-3"></div>
        <SummaryRow
          icon={<div className=""><MoneyReceive /></div>}
          title={AppStrings.CALCULATION_SECTION.THEY_RECEIVE}
          amount={formatNumber(calculator?.getTransactionSummary?.data?.reciepient_recieve)}
          currency={calculator?.getTransactionSummary?.data?.dest_currency}
          loading={calculator?.getTransactionSummary?.loading || calculator?.fxRates?.loading || calculator?.cardRate?.loading}
        />

      </div>

      {/* FOOTER BUTTONS */}
      <div className={styles.calculationSection.footer}>
        <Button
          bgColor={proceedButtonDisable ? "bg-[#eee3c4]" : "bg-[#CFAF58]"}
          fontStyle={{
            fontFamily: "font-roboto",
            fontSize: "text-base",
            fontWeight: "font-bold",
            fontColor: "text-white",
          }}
          borderStyle={{
            borderRadius: "rounded",
          }}
          onClickHandler={() => setShowQuoteModal(true)}
          className={"flex justify-center py-4 px-14 hover:scale-105 transition-all max-w-100"}
          disabled={proceedButtonDisable}
        >
          {AppStrings.CALCULATION_SECTION.USER_QUOTE}
        </Button>

        <Button
          bgColor={proceedButtonDisable ? "bg-gray-300" : "bg-primary-1050"}
          fontStyle={{
            fontFamily: "font-roboto",
            fontSize: "text-base",
            fontWeight: "font-bold",
            fontColor: "text-white",
          }}
          borderStyle={{
            borderRadius: "rounded",
          }}
          isLoading={calculator?.getLockBookRate?.loading}
          onClickHandler={sendMoneyProceed}
          className={"flex justify-center py-4 px-14 hover:scale-105 transition-all max-w-100"}
          disabled={proceedButtonDisable}
        >
          {AppStrings.CALCULATION_SECTION.PROCEED}
        </Button>
      </div>

      {showQuoteModal && (
        <QuoteManagerModal
          onClose={() => setShowQuoteModal(false)}
        />
      )}

    </div>
  );
};

export default CalculationSection;
